/**
 * @file routes/_app/audit-reports/pending-review.tsx
 * @description 待評審報告列表頁（序17）。
 *
 * 用途：給 admin / manager 一個「一站式」入口，集中查看所有狀態為「評審中」的稽核報告，
 *      不必逐案進入案件詳情才能發現「有報告在等我核准」。
 *
 * 資料源：GET /api/audit-reports/pending-review（capability audit-report.review）。
 * 導航：每筆「前往評審」→ /audit-tasks/:auditTaskId?phase=report（PhaseReport 內核准/退回）；
 *      若後端未回 auditTaskId（極少數無稽核任務的舊資料），回落到 /dispatch/:caseId。
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ClipboardCheck, ArrowRight } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { ListPageShell, EmptyState } from '@/components/shell';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/audit-reports/pending-review')({
  component: PendingReviewPage,
});

/** 待評審報告項（對應後端 GET /api/audit-reports/pending-review 的 items） */
interface PendingReviewItem {
  reportId: number;
  caseId: number;
  reportCode: string;
  caseCode: string;
  clientName: string | null;
  teamLeadName: string | null;
  versionNo: number | null;
  submittedAt: string | null;
  /** 關聯稽核任務 ID（供導航進報告階段；可能為 null） */
  auditTaskId: number | null;
}

function PendingReviewPage() {
  const navigate = useNavigate();

  // 拉取待評審報告清單（能力不足時後端 403，由全域錯誤處理提示）
  const { data, isLoading } = useQuery<{ items: PendingReviewItem[]; total: number }>({
    queryKey: ['audit-reports', 'pending-review'],
    queryFn: () => apiClient.get('/api/audit-reports/pending-review'),
  });

  const items = data?.items ?? [];

  /** 導航到報告評審位置：優先進稽核任務報告階段，否則回落案件詳情 */
  const goReview = (row: PendingReviewItem) => {
    if (row.auditTaskId != null) {
      void navigate({
        to: '/audit-tasks/$taskId',
        params: { taskId: String(row.auditTaskId) },
        search: { phase: 'report' },
      });
    } else {
      void navigate({ to: '/dispatch/$caseId', params: { caseId: String(row.caseId) } });
    }
  };

  const columns: ColumnsType<PendingReviewItem> = [
    {
      title: '案件編號',
      dataIndex: 'caseCode',
      key: 'caseCode',
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '報告編號',
      dataIndex: 'reportCode',
      key: 'reportCode',
      render: (v: string) => <span style={{ fontFamily: 'monospace' }}>{v}</span>,
    },
    {
      title: '客戶',
      dataIndex: 'clientName',
      key: 'clientName',
      render: (v: string | null) => v ?? '—',
    },
    {
      title: '主導稽核員',
      dataIndex: 'teamLeadName',
      key: 'teamLeadName',
      render: (v: string | null) => v ?? <Tag color="orange">未指派</Tag>,
    },
    {
      title: '版本',
      dataIndex: 'versionNo',
      key: 'versionNo',
      render: (v: number | null) => (v != null ? `v${v}` : '—'),
    },
    {
      title: '提交時間',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, row: PendingReviewItem) => (
        <Button
          type="primary"
          size="small"
          icon={<ArrowRight size={14} />}
          onClick={() => goReview(row)}
        >
          前往評審
        </Button>
      ),
    },
  ];

  return (
    <ListPageShell
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ClipboardCheck size={18} />
          待評審報告
        </span>
      }
      description="集中查看所有已由稽核員提交、狀態為「評審中」的稽核報告，點擊「前往評審」直達報告階段核准或退回。"
      meta={
        <Typography.Text type="secondary">
          共 <b>{data?.total ?? 0}</b> 份待評審
        </Typography.Text>
      }
    >
      <Table<PendingReviewItem>
        rowKey="reportId"
        loading={isLoading}
        columns={columns}
        dataSource={items}
        pagination={false}
        locale={{
          emptyText: (
            <EmptyState
              title="目前沒有待評審的報告"
              description="當稽核員提交報告進入評審後，會自動出現在這裡。"
            />
          ),
        }}
      />
    </ListPageShell>
  );
}
